import { Navigate, Route, Routes } from 'react-router-dom';

import { AppShell } from '@/components/layout/AppShell';
import { LoadingState } from '@/components/ui/states';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';
import { useT } from '@/lib/i18n';
import {
  AssignedByMeRoute,
  AssignedToMeRoute,
  BacklogRoute,
  InvolvedRoute,
} from '@/routes/ListRoutes';
import { CompletedRoute } from '@/routes/Completed';
import { ClientDetailRoute, ClientsRoute } from '@/routes/Clients';
import { GuideRoute } from '@/routes/Guide';
import { ProjectsRoute } from '@/routes/Projects';
import { ReportPrintRoute } from '@/routes/ReportPrint';
import { BoardRoute } from '@/routes/BoardRoute';
import { KbIndexRoute, KbLayout } from '@/routes/kb/KbLayout';
import { KbNoteRoute } from '@/routes/kb/KbNoteRoute';
import { KbTopicRoute } from '@/routes/kb/KbTopicRoute';
import { LabelRoute } from '@/routes/LabelRoute';
import { LoginRoute } from '@/routes/Login';
import { OnboardingRoute } from '@/routes/Onboarding';
import { AcceptInviteRoute } from '@/routes/AcceptInvite';
import { InviteRoute } from '@/routes/Invite';
import { SettingsRoute } from '@/routes/Settings';
import { StatsRoute } from '@/routes/Stats';
import { TeamsRoute } from '@/routes/Teams';
import { TodayRoute } from '@/routes/Today';
import { UpcomingRoute } from '@/routes/Upcoming';

/**
 * Routing follows the session, in three states:
 *
 *   not signed in  → the sign-in screen only
 *   no username    → onboarding only, so no half-registered account can create
 *                    data referencing a handle nobody has confirmed
 *   signed in      → the application
 *
 * Each state renders a closed set of routes rather than redirecting from inside
 * the app, so there is no window in which a protected screen mounts and fires
 * queries it is not entitled to make.
 */
export function App() {
  const t = useT();
  const { isLoading, isError, isAuthenticated, needsOnboarding, user, refetch } =
    useAuth();

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <LoadingState />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center">
        <p className="text-sm text-content-muted">{t('error.offline')}</p>
        <Button variant="secondary" size="sm" onClick={() => void refetch()}>
          {t('common.retry')}
        </Button>
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    return (
      <Routes>
        {/* Before the sign-in screen: an invitation must be able to say what
            it is for, or the link drops a stranger at a button that may
            refuse them without explaining why. */}
        <Route path="/invite/:token" element={<InviteRoute />} />
        <Route path="/login" element={<LoginRoute />} />
        <Route path="*" element={<LoginRoute />} />
      </Routes>
    );
  }

  if (needsOnboarding) {
    return (
      <Routes>
        <Route path="*" element={<OnboardingRoute />} />
      </Routes>
    );
  }

  return (
    <Routes>
      {/* Outside the shell: a printed protocol carries no sidebar. */}
      <Route path="/reports/:reportId/print" element={<ReportPrintRoute />} />
      {/* Following an invite link while already signed in. Redirecting to
          /teams was indistinguishable from a broken link. */}
      <Route
        path="/invite/:token"
        element={<AcceptInviteRoute email={user.email} />}
      />
      <Route element={<AppShell user={user} />}>
        <Route path="/today" element={<TodayRoute />} />
        <Route path="/upcoming" element={<UpcomingRoute />} />
        <Route path="/backlog" element={<BacklogRoute />} />
        <Route path="/board" element={<BoardRoute />} />
        <Route path="/assigned-to-me" element={<AssignedToMeRoute />} />
        <Route path="/assigned-by-me" element={<AssignedByMeRoute />} />
        <Route path="/involved" element={<InvolvedRoute />} />
        <Route path="/completed" element={<CompletedRoute />} />
        <Route path="/label/:labelId" element={<LabelRoute />} />
        <Route path="/kb" element={<KbLayout />}>
          <Route index element={<KbIndexRoute />} />
          <Route path="notes/:noteId" element={<KbNoteRoute />} />
          <Route path=":topicId" element={<KbTopicRoute />} />
        </Route>
        <Route path="/settings" element={<SettingsRoute />} />
        <Route path="/teams" element={<TeamsRoute />} />
        <Route path="/stats" element={<StatsRoute />} />
        <Route path="/guide" element={<GuideRoute />} />
        <Route path="/projects" element={<ProjectsRoute />} />
        <Route path="/clients" element={<ClientsRoute />} />
        <Route path="/clients/:clientId" element={<ClientDetailRoute />} />
        {/* Today is the front door — the spec's primary screen. */}
        <Route path="*" element={<Navigate to="/today" replace />} />
      </Route>
    </Routes>
  );
}
